import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { useInView } from "react-intersection-observer";
import { ScrollSync } from "react-scroll-sync";
import {
	SortingRule,
	useFilters,
	usePagination,
	useRowSelect,
	useRowState,
	useSortBy,
	useTable,
} from "react-table";

import { usePrevious } from "hooks";
import { SortBy } from "types";

import { TableBody } from "./body";
import { TableFooter } from "./footer";
import { TableHeader } from "./header";
import { WithLoader } from "../skeleton/withLoader";

import styles from "./table.module.scss";

const PAGE_SIZE = 50;

const defaultProps = {
	meta: { loading: false, totalItems: 0 },
	initialState: {},
	defaultColumn: {},
	disableHeader: false,
	hideFooter: false,
	stickyFooter: false,
	stickyHeader: false,
	compact: false,
	transparent: false,
	hover: false,
	bordered: "",
};

type sizeType = "sm";

type TableProps = {
	columns: any;

	className?: string;
	classNameHeader?: string;
	classNameFooter?: string;
	tableRowClassName?: string;
	minWidth?: string;
	isTotalCount?: boolean;
	isOddRowBackground?: boolean;
	disableFilters?: boolean;
	manualFilters?: boolean;
	scrollToTop?: boolean;
	disableHeader?: boolean;
	isInfinityScrollEnabled?: boolean;
	bordered?: "full" | "horizontal";
	handlePageChange?: Function;
	handleSortChange?: Function;
	handleRowSelect?: Function;
	scrollNextPage?: Function;
	onRowClick?: (row) => void;
	handleRowSubmit?: ({
		values,
		row,
		setRowState,
	}: {
		values: { [key: string]: any };
		row: { [key: string]: any };
		setRowState: any;
	}) => any;

	data: Array<{}>;
	customStyle?: Object;
	meta?: typeof defaultProps.meta;
	initialState?: {
		pageIndex?: number;
		pageSize?: number;
		sortBy?: SortBy[];
	};
	defaultColumn?: { sortDescFirst?: boolean };
	hideFooter?: typeof defaultProps.hideFooter;
	stickyFooter?: typeof defaultProps.stickyFooter;
	stickyHeader?: typeof defaultProps.stickyHeader;
	compact?: typeof defaultProps.compact;
	transparent?: typeof defaultProps.transparent;
	hover?: typeof defaultProps.hover;
	size?: sizeType;
	customRowClassName?: (row: any) => string | false;
	updatedSortBy?: { id: string; desc: boolean }[];
	pageSize?: number;
};

// 1. Before and after any update sync code between projects
// Currently it's "RiseCRM.PortalClient" and "Web Merchant"

// 2. Fills parent. To limit height set height of the parent

// 3. Some optimiations might be needed. Along with extensions
const Table = ({
	columns,

	handlePageChange,
	handleSortChange,
	handleRowSelect,
	onRowClick,
	handleRowSubmit,
	data,
	updatedSortBy,
	meta: { loading, totalItems } = defaultProps.meta,

	className,
	classNameHeader,
	classNameFooter,
	customStyle,
	tableRowClassName,
	initialState,
	defaultColumn,
	hideFooter = false,
	stickyFooter = false,
	stickyHeader = false,
	compact = false,
	transparent = false,
	hover = false,
	size,
	minWidth,
	disableFilters = true,
	manualFilters = true,
	scrollToTop = false,
	disableHeader = false,
	customRowClassName,
	bordered,
	isOddRowBackground = false,
	isInfinityScrollEnabled = false,
	scrollNextPage,
	pageSize = PAGE_SIZE,
}: TableProps &
	React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	>): JSX.Element => {
	const headerRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);

	const [horizontalScroll, setHorizontalScroll] = useState<number>(0);

	const pageCount = useMemo(
		() => Math.ceil(totalItems / pageSize) || 1,
		[totalItems, pageSize],
	);

	const { ref, inView } = useInView({
		triggerOnce: true,
		delay: 500,
	});

	useEffect(() => {
		if (inView && isInfinityScrollEnabled) {
			scrollNextPage?.();
		}
	}, [scrollNextPage, inView, isInfinityScrollEnabled]);

	// Return horizontal scroll to the previous position when sorting is changed
	useEffect(() => {
		if (headerRef.current) {
			headerRef.current.parentElement?.scrollTo({
				left: horizontalScroll,
				behavior: "auto",
			});
		} else if (bodyRef.current) {
			bodyRef.current.parentElement?.scrollTo({
				left: horizontalScroll,
				behavior: "auto",
			});
		}
	}, [headerRef, bodyRef, horizontalScroll, loading]);

	const tableContainerRef = useRef<HTMLDivElement>(null);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
		page,
		gotoPage,
		setRowState,
		setSortBy,
		state: { pageIndex, sortBy, selectedRowIds },
	}: { [key: string]: any } = useTable(
		{
			columns,
			data,

			initialState: {
				pageIndex: 0,
				pageSize,
				...initialState, // Be cearful as this might override your state when data changes
			} as any,
			defaultColumn: {
				sortDescFirst: true, // That's the way all tables are currently setting order.
				...defaultColumn,
			},

			manualSortBy: true,
			disableSortRemove: true, // Sort must me selected on one of the columns
			disableMultiSort: true, // Doesn't exist on the api

			manualPagination: true,
			pageCount: pageCount,

			manualFilters,
			disableFilters,
		},
		useRowState,
		useFilters,
		useSortBy,
		usePagination,
		useRowSelect,
	);

	const prevInitState = usePrevious<{
		initialState?: {
			pageIndex?: number | undefined;
			pageSize?: number | undefined;
			sortBy?: SortBy[] | undefined;
		};
		sortBy: SortingRule<{}>[];
		pageIndex: number;
	}>({
		initialState,
		sortBy,
		pageIndex,
	});

	useEffect(() => {
		if (
			handlePageChange &&
			!fp.isEqual(pageIndex, fp.get("pageIndex", initialState))
		) {
			if (prevInitState?.pageIndex !== pageIndex) {
				handlePageChange({ pageIndex, pageSize });
				gotoPage(pageIndex);
			} else if (
				prevInitState?.initialState?.pageIndex !==
				initialState?.pageIndex
			) {
				handlePageChange({
					pageIndex: initialState?.pageIndex,
					pageSize,
				});
				gotoPage(initialState?.pageIndex || 0);
			}

			const navbar = document.getElementsByClassName("navbar")[0];

			if (scrollToTop && tableContainerRef?.current && navbar) {
				window.scrollTo(
					window.scrollX,
					tableContainerRef.current.offsetTop - navbar.clientHeight,
				);
			}
		}
	}, [
		handlePageChange,
		pageIndex,
		initialState,
		prevInitState?.initialState,
		prevInitState?.pageIndex,
		gotoPage,
		scrollToTop,
		pageSize,
	]);

	useEffect(() => {
		if (handleSortChange) {
			if (
				prevInitState?.initialState === initialState &&
				prevInitState?.sortBy !== sortBy
			) {
				// Return horizontal scroll to the previous position when sorting is changed
				const horizontalScroll =
					headerRef.current?.parentElement?.scrollLeft ||
					bodyRef.current?.parentElement?.scrollLeft;

				if (horizontalScroll !== undefined) {
					setHorizontalScroll(horizontalScroll);
				}

				handleSortChange({ sortBy });
			}
		}
	}, [
		handleSortChange,
		sortBy,
		initialState,
		prevInitState?.initialState,
		prevInitState?.sortBy,
	]);

	useEffect(() => {
		if (handleRowSelect) {
			handleRowSelect(rows, selectedRowIds);
		}
	}, [rows, handleRowSelect, selectedRowIds]);

	useEffect(() => {
		if (updatedSortBy) {
			if (!fp.isEqual(sortBy, fp.get("sortBy", initialState))) {
				setSortBy(updatedSortBy);
			}
		}
	}, [setSortBy, sortBy, data, initialState, updatedSortBy]);

	const clickableRowClass =
		onRowClick && fp.isFunction(onRowClick) && styles.clickableRow;
	const stickyFooterClass = stickyFooter && styles.stickyFooter;
	const stickyHeaderClass = stickyHeader && styles.stickyHeader;
	const compactTableClass = compact && styles.compact;
	const transparentTableClass = transparent && styles.transparent;
	const hoverTableClass = hover && styles.hover;
	const smTableClass = size === "sm" && styles.tableSm;
	const borderedClass = bordered === "horizontal" && styles.bordered;
	const borderedFullClass = bordered === "full" && styles.borderedFull;

	return (
		<div
			{...getTableProps()}
			className={clsx(
				styles.table,
				className,
				clickableRowClass,
				stickyFooterClass,
				stickyHeaderClass,
				compactTableClass,
				transparentTableClass,
				hoverTableClass,
				smTableClass,
				borderedClass,
				borderedFullClass,
			)}
			style={customStyle}
			ref={tableContainerRef}
		>
			<ScrollSync>
				<WithLoader
					variant="table"
					loading={loading}
					isHideFooterLoading={hideFooter}
					isHideHeaderLoading={disableHeader}
				>
					{!disableHeader && (
						<TableHeader
							header={headerGroups[0]}
							minWidth={minWidth}
							classNameHeader={classNameHeader}
							ref={headerRef}
						/>
					)}
					<TableBody
						setRowState={setRowState}
						getTableBodyProps={getTableBodyProps}
						prepareRow={prepareRow}
						onRowClick={onRowClick}
						handleRowSubmit={handleRowSubmit}
						rows={page}
						minWidth={minWidth}
						customRowClassName={customRowClassName}
						tableRowClassName={tableRowClassName}
						isOddRowBackground={isOddRowBackground}
						scrollRef={ref}
						ref={bodyRef}
						isInfinityScrollEnabled={isInfinityScrollEnabled}
					/>
					{!hideFooter && (
						<TableFooter
							classNameFooter={classNameFooter}
							pageIndex={pageIndex}
							totalItems={totalItems}
							gotoPage={gotoPage}
							pageSize={pageSize}
						/>
					)}
				</WithLoader>
			</ScrollSync>
		</div>
	);
};

export default memo(Table);
